import React, { useMemo, useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Mail,
  Lock,
  LayoutDashboard,
  Sparkles,
  CalendarDays,
  Lightbulb,
  MessageSquarePlus,
  CreditCard,
  LogOut,
  Menu,
  Instagram,
  Gem,
  Rocket,
  Bell,
  Library,
  LayoutGrid,
  Link2,
  LifeBuoy,
  ShieldCheck,
  BookOpen,
  Building2,
  Users,
  ChevronDown,
  Search,
  MessageSquare,
  ListChecks,
  Megaphone,
  Video,
  HandCoins,
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { useAuth } from "@/lib/auth";
import ConnectionPill from "@/components/ConnectionPill";
import UpdateBanner from "@/components/UpdateBanner";
import WorkspaceSelector from "@/components/WorkspaceSelector";
import { useReleaseNotes } from "@/hooks/useReleaseNotes";
import api from "@/lib/api";

// Grouped nav. Each section id is used as the sessionStorage key for its
// collapsed/expanded state. Two kinds of items:
//   * Live route → { to, label, icon, testid }
//   * Coming Soon (locked, non-navigable, no backend call) →
//       { key, label, icon, testid, comingSoon: true }
// The Coming Soon variant uses the same "Soon" lock badge as existing
// Coming Soon items but renders as a disabled <button> instead of a
// <Link>, so clicking it can never produce a 404 or navigate to a
// nonexistent page.
const NAV_SECTIONS = [
  {
    id: "social-media-management",
    label: "Social media management",
    items: [
      { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
      { to: "/content", label: "Content Manager", icon: LayoutGrid, testid: "nav-content" },
      { to: "/generate", label: "Generate Post", icon: Sparkles, testid: "nav-generate-post" },
      { key: "ai-video-creation", label: "AI Video Creation", icon: Video, testid: "nav-ai-video-creation", comingSoon: true },
      { to: "/autopilot", label: "Autopilot", icon: Rocket, testid: "nav-autopilot" },
      { to: "/calendar", label: "Content Calendar", icon: CalendarDays, testid: "nav-content-calendar" },
      { to: "/brand-learning", label: "Brand Learning", icon: BookOpen, testid: "nav-brand-learning" },
      { to: "/settings/connections", label: "Connections", icon: Link2, testid: "nav-connections" },
      { key: "dm-community", label: "DM Bot / Community Management", icon: MessageSquare, testid: "nav-dm-community", comingSoon: true },
    ],
  },
  {
    id: "business",
    label: "Business",
    items: [
      { to: "/media", label: "Media Library", icon: Library, testid: "nav-media-library" },
      { to: "/settings/business-profile", label: "Business Profile", icon: Building2, testid: "nav-business-profile" },
      { to: "/team", label: "Team", icon: Users, testid: "nav-team" },
      { key: "competitor-analysis", label: "Competitor Analysis", icon: Search, testid: "nav-competitor-analysis", comingSoon: true },
      { key: "task-calendar", label: "Task & Calendar Management", icon: ListChecks, testid: "nav-task-calendar", comingSoon: true },
    ],
  },
  {
    id: "marketing",
    label: "Marketing",
    items: [
      { to: "/ideas", label: "Marketing Ideas", icon: Lightbulb, testid: "nav-marketing-ideas" },
      { key: "email-marketing", label: "Email Marketing", icon: Mail, testid: "nav-email-marketing", comingSoon: true },
      { key: "ad-creations", label: "Ad Creations", icon: Megaphone, testid: "nav-ad-creations", comingSoon: true },
    ],
  },
  {
    id: "other",
    label: "Other",
    items: [
      { to: "/billing", label: "Billing", icon: CreditCard, testid: "nav-billing" },
      { to: "/affiliate-program", label: "Affiliate Program", icon: HandCoins, testid: "nav-affiliate-program" },
      { to: "/feedback", label: "Feedback & Ideas", icon: MessageSquarePlus, testid: "nav-feedback" },
      { to: "/settings/email-preferences", label: "Email Preferences", icon: Mail, testid: "nav-email-preferences" },
      { to: "/settings/security", label: "Security", icon: ShieldCheck, testid: "nav-security" },
      { to: "/updates", label: "What's New", icon: Sparkles, testid: "nav-whats-new", releaseIndicator: true },
    ],
  },
  {
    id: "help",
    label: "Help",
    items: [
      { to: "/support", label: "Support", icon: LifeBuoy, testid: "nav-support" },
    ],
  },
];

const SESSION_KEY = "postmoro.sidebar.collapsed.v1";

function readCollapsedFromSession() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed ? parsed : {};
  } catch (_e) { return {}; }
}

function writeCollapsedToSession(state) {
  try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(state || {})); } catch (_e) { /* ignore */ }
}

function NavLinkItem({ item, active, hasReleaseUnread, onNavigate }) {
  const Icon = item.icon;
  const isLocked = !!item.comingSoon;
  const showReleaseDot = item.releaseIndicator && hasReleaseUnread && !active;
  if (isLocked) {
    // Non-navigable Coming Soon item. Uses the same visual as active nav
    // items minus the active-highlight, and shows the "Soon" lock badge
    // already used elsewhere. NEVER renders a <Link>, so clicking it
    // cannot navigate, cannot 404, and cannot hit the backend.
    return (
      <button
        type="button"
        disabled
        aria-disabled="true"
        data-testid={item.testid}
        title="Coming Soon"
        className="group w-full flex items-center gap-3 rounded-md px-3 py-2 text-sm text-[color:var(--text-3)] cursor-not-allowed opacity-70"
      >
        <Icon className="h-4 w-4 shrink-0" />
        <span className="truncate text-left">{item.label}</span>
        <span
          data-testid={`${item.testid}-lock-badge`}
          className="ml-auto inline-flex items-center gap-1 rounded-full border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-[color:var(--text-3)]"
        >
          <Lock className="h-2.5 w-2.5" />
          Soon
        </span>
      </button>
    );
  }
  return (
    <Link
      to={item.to}
      data-testid={item.testid}
      onClick={() => onNavigate?.()}
      className={
        "group flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-all " +
        (active
          ? "bg-[color:var(--surface-2)] text-[color:var(--text-1)] shadow-[inset_0_0_0_1px_rgba(27,168,159,0.35)]"
          : "text-[color:var(--text-2)] hover:text-[color:var(--text-1)] hover:bg-[color:var(--surface-1)]")
      }
    >
      <Icon className={"h-4 w-4 shrink-0 " + (active ? "text-[color:var(--brand-primary)]" : "")} />
      <span className="truncate">{item.label}</span>
      {showReleaseDot ? (
        <span
          data-testid={`${item.testid}-unread-badge`}
          className="ml-auto inline-flex items-center gap-1 rounded-full border border-[color:rgba(27,168,159,0.35)] bg-[color:rgba(27,168,159,0.10)] px-1.5 py-0.5 text-[9px] uppercase tracking-[0.14em] text-[color:var(--brand-primary)] font-semibold"
          aria-label="New release available"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--brand-primary)] animate-pulse" />
          New
        </span>
      ) : active ? <span className="ml-auto h-1.5 w-1.5 rounded-full bg-[color:var(--brand-primary)]" /> : null}
    </Link>
  );
}

function SidebarSection({ section, activePath, hasReleaseUnread, collapsed, onToggle, onNavigate }) {
  const hasActive = section.items.some((i) => i.to && activePath.startsWith(i.to));
  const isCollapsed = collapsed && !hasActive; // auto-expand if active item lives here
  return (
    <div className="mb-2" data-testid={`nav-section-${section.id}`}>
      <button
        type="button"
        onClick={() => onToggle?.(section.id)}
        aria-expanded={!isCollapsed}
        data-testid={`nav-section-toggle-${section.id}`}
        className="w-full flex items-center justify-between px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] text-[color:var(--text-3)] hover:text-[color:var(--text-2)] rounded transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--brand-primary)]/40"
      >
        <span>{section.label}</span>
        <ChevronDown className={"h-3 w-3 transition-transform " + (isCollapsed ? "-rotate-90" : "")} aria-hidden="true" />
      </button>
      {!isCollapsed && (
        <div className="mt-1 space-y-0.5" data-testid={`nav-section-items-${section.id}`}>
          {section.items.map((item) => (
            <NavLinkItem
              key={item.to || item.key}
              item={item}
              active={item.to ? activePath.startsWith(item.to) : false}
              hasReleaseUnread={hasReleaseUnread}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SidebarBody({ onNavigate }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdminEligible } = useAuth();
  const { hasUnread: hasReleaseUnread } = useReleaseNotes();
  const [collapsedMap, setCollapsedMap] = useState(() => readCollapsedFromSession());
  // Ticket 5.2 — owner/dev bypass for the Email Marketing sidebar item.
  // Reuses the same backend authorization source as _require_email_marketing
  // (GET /api/email/access → { allowed: true } for the owner allowlist).
  // Frontend visibility is NOT authorization — backend endpoints remain
  // gated. This only removes the "SOON" badge for allowlisted owners so
  // they can actually navigate into the page they already have access to.
  const [emailMarketingAccess, setEmailMarketingAccess] = useState(null);
  useEffect(() => {
    if (!user) { setEmailMarketingAccess(null); return; }
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/email/access");
        if (alive) setEmailMarketingAccess(data);
      } catch (_e) {
        if (alive) setEmailMarketingAccess({ allowed: false });
      }
    })();
    return () => { alive = false; };
  }, [user?.user_id, user?.email]);
  const emailMarketingUnlocked = !!emailMarketingAccess?.allowed;

  const toggleSection = (id) => {
    setCollapsedMap((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      writeCollapsedToSession(next);
      return next;
    });
  };

  const initials = useMemo(
    () => (user?.name?.charAt(0)?.toUpperCase() || "U"),
    [user?.name]
  );

  return (
    <div className="flex h-full w-full flex-col relative" data-testid="app-sidebar">
      {/* A. FIXED TOP BRANDING */}
      <div aria-hidden="true" className="absolute inset-x-0 top-0 h-40 pointer-events-none" style={{ background: "radial-gradient(420px 200px at 30% 0%, rgba(27,168,159,0.16), transparent 70%)" }} />
      <div className="relative shrink-0 flex items-center gap-2 px-5 py-4 border-b border-[color:var(--border-subtle)]" data-testid="app-sidebar-brand">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-[color:var(--brand-primary)] to-[color:var(--brand-secondary)] text-[color:var(--bg-0)] shadow-[0_4px_18px_rgba(27,168,159,0.45)]">
          <Gem className="h-4 w-4" />
        </div>
        <div>
          <div className="font-display text-lg font-semibold tracking-tight leading-none">Postmoro</div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--text-3)] mt-1">AI Marketing OS</div>
        </div>
      </div>

      {/* B. SCROLLABLE NAVIGATION AREA */}
      <nav
        className="relative flex-1 min-h-0 overflow-y-auto px-3 py-3"
        data-testid="app-sidebar-nav"
        aria-label="Primary"
        style={{ scrollbarGutter: "stable" }}
      >
        {NAV_SECTIONS.map((section) => {
          // Business-Teams V1: hide the Billing entry for workspace
          // members. The owner and standalone users see it as usual.
          // Backend authoritatively enforces this via
          // _require_workspace_owner — this is UX only.
          const isMember = user?.workspace_role === "member";
          let filteredSection = isMember
            ? { ...section, items: section.items.filter((i) => i.testid !== "nav-billing") }
            : section;
          // Ticket 5.2 — swap the email-marketing lock for a real route
          // when the owner-access endpoint says this user is allowed.
          if (emailMarketingUnlocked && filteredSection.items?.some((i) => i.testid === "nav-email-marketing")) {
            filteredSection = {
              ...filteredSection,
              items: filteredSection.items.map((i) => (
                i.testid === "nav-email-marketing"
                  ? { to: "/email", label: i.label, icon: i.icon, testid: i.testid }
                  : i
              )),
            };
          }
          return (
            <SidebarSection
              key={section.id}
              section={filteredSection}
              activePath={location.pathname}
              hasReleaseUnread={hasReleaseUnread}
              collapsed={!!collapsedMap[section.id]}
              onToggle={toggleSection}
              onNavigate={onNavigate}
            />
          );
        })}
      </nav>

      {/* C. FIXED BOTTOM — Admin (owner only) + Connections + User */}
      <div className="relative shrink-0 border-t border-[color:var(--border-subtle)] bg-[color:var(--bg-1)]" data-testid="app-sidebar-bottom">
        {isAdminEligible ? (
          <div className="px-3 pt-3">
            <Link
              to="/admin"
              onClick={() => onNavigate?.()}
              data-testid="nav-admin-console"
              className="flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-all text-[color:var(--accent-warm)] border border-[color:rgba(255,138,61,0.30)] bg-[color:rgba(255,138,61,0.05)] hover:bg-[color:rgba(255,138,61,0.10)]"
            >
              <ShieldCheck className="h-4 w-4" />
              <span>Admin Console</span>
              <span className="ml-auto text-[9px] uppercase tracking-wider">Owner</span>
            </Link>
          </div>
        ) : null}
        <div className="px-3 pt-3">
          <ConnectionPill />
        </div>
        <div className="p-3">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="h-8 w-8 rounded-full bg-gradient-to-br from-[color:var(--brand-primary)] to-[color:var(--brand-secondary)] flex items-center justify-center text-xs font-semibold text-[color:var(--bg-0)]">
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-[color:var(--text-1)] truncate">{user?.name}</div>
              <div className="text-xs text-[color:var(--text-3)] truncate">{user?.email}</div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              data-testid="logout-button"
              onClick={async () => {
                await logout();
                navigate("/", { replace: true });
              }}
              className="text-[color:var(--text-2)] hover:text-[color:var(--text-1)]"
              aria-label="Log out"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AppShell({ children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen bg-[color:var(--bg-0)] text-[color:var(--text-1)] flex">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:w-[264px] md:flex-col fixed inset-y-0 left-0 bg-[color:var(--bg-1)] border-r border-[color:var(--border-subtle)] overflow-hidden">
        <SidebarBody />
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-[color:var(--bg-1)]/95 backdrop-blur border-b border-[color:var(--border-subtle)] px-3 h-14 flex items-center gap-2">
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="Open menu" data-testid="mobile-menu-toggle">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-[280px] bg-[color:var(--bg-1)] border-r border-[color:var(--border-subtle)]">
            <SidebarBody onNavigate={() => setOpen(false)} />
          </SheetContent>
        </Sheet>
        <div className="flex items-center gap-2">
          <div className="flex h-6 w-6 items-center justify-center rounded-md bg-gradient-to-br from-[color:var(--brand-primary)] to-[color:var(--brand-secondary)] text-[color:var(--bg-0)]">
            <Instagram className="h-3 w-3" />
          </div>
          <span className="font-display text-base font-semibold">Postmoro</span>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {/* Mobile-only mount — desktop mount is above the main content */}
          <div className="md:hidden" data-testid="workspace-selector-mobile-container">
            <WorkspaceSelector />
          </div>
          <Bell className="h-4 w-4 text-[color:var(--text-3)]" />
        </div>
      </div>

      <main className="flex-1 min-w-0 md:ml-[264px] pt-14 md:pt-0 min-h-screen flex flex-col">
        <UpdateBanner />
        {/* Desktop-only mount — right-aligned above the page content. */}
        <div className="hidden md:flex justify-end px-4 sm:px-6 lg:px-10 pt-4" data-testid="workspace-selector-desktop-container">
          <WorkspaceSelector />
        </div>
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ type: "spring", stiffness: 420, damping: 34, mass: 0.9 }}
          className="px-4 sm:px-6 lg:px-10 py-6 sm:py-8 max-w-[1320px] mx-auto w-full flex-1"
        >
          {children}
        </motion.div>
        <footer className="mt-auto px-4 sm:px-6 lg:px-10 py-4 border-t border-[color:var(--border-subtle)] text-[11px] text-[color:var(--text-3)]">
          <div className="max-w-[1320px] mx-auto flex flex-wrap items-center gap-4">
            <span>© {new Date().getFullYear()} Postmoro</span>
            <Link to="/privacy" className="hover:text-[color:var(--text-1)]" data-testid="app-footer-privacy">Privacy</Link>
            <Link to="/terms" className="hover:text-[color:var(--text-1)]" data-testid="app-footer-terms">Terms</Link>
            <Link to="/data-deletion" className="hover:text-[color:var(--text-1)]" data-testid="app-footer-data">Data Deletion</Link>
            <Link to="/support" className="hover:text-[color:var(--text-1)]" data-testid="app-footer-support">Support</Link>
            <Link to="/updates" className="hover:text-[color:var(--text-1)]" data-testid="app-footer-whats-new">What&apos;s New</Link>
            <a href="mailto:support@postmoro.com" className="hover:text-[color:var(--text-1)]" data-testid="app-footer-contact">Contact</a>
          </div>
        </footer>
      </main>
    </div>
  );
}
